import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Users, FileText, CheckCircle, Clock, AlertCircle, XCircle, Eye,
  BarChart3, TrendingUp, Calendar, DollarSign, PenTool, Download,
  Shield, UserCheck, Building2, FileCheck, Printer, RefreshCw
} from 'lucide-react';

const STATUS_LABELS = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  ready: 'Pronto',
  completed: 'Concluído'
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  ready: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-700'
};

export default function ConsulDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [financialData, setFinancialData] = useState(null);
  const [reports, setReports] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [signatureNotes, setSignatureNotes] = useState('');
  const [signing, setSigning] = useState(false);
  const [reportPeriod, setReportPeriod] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, pendingRes, finRes] = await Promise.all([
        api.get('/consul/dashboard'),
        api.get('/consul/pending-signatures'),
        api.get('/consul/financial-overview?period=month')
      ]);
      setDashboard(dashRes.data);
      setPendingSignatures(pendingRes.data);
      setFinancialData(finRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSignCard = async (cardId) => {
    setSigning(true);
    try {
      await api.post(`/consular-cards/${cardId}/sign-and-emit`);
      toast.success('Cartao assinado e emitido com sucesso! Email enviado ao cidadao.');
      setSelectedCard(null);
      setSignatureNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao assinar cartao');
    } finally {
      setSigning(false);
    }
  };

  const generateReport = async () => {
    try {
      const params = new URLSearchParams();
      if (reportPeriod.start) params.append('start_date', reportPeriod.start);
      if (reportPeriod.end) params.append('end_date', reportPeriod.end);
      const res = await api.get(`/consul/reports?${params.toString()}`);
      setReports(res.data);
      toast.success('Relatório gerado com sucesso');
    } catch (err) {
      toast.error('Erro ao gerar relatório');
    }
  };

  const fetchFinancialPeriod = async (period) => {
    try {
      const res = await api.get(`/consul/financial-overview?period=${period}`);
      setFinancialData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar dados financeiros');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="text-purple-600" size={28} />
            Painel do Cônsul
          </h1>
          <p className="text-sm text-gray-500 mt-1">Supervisão consular e gestão de processos</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
          <RefreshCw size={16} /> Actualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {[
          { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
          { id: 'signatures', label: 'Assinaturas Pendentes', icon: PenTool },
          { id: 'agents', label: 'Supervisão de Agentes', icon: Users },
          { id: 'financial', label: 'Dados Financeiros', icon: DollarSign },
          { id: 'reports', label: 'Relatórios', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap ${
              activeTab === tab.id ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.id === 'signatures' && pendingSignatures.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">{pendingSignatures.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={FileText} label="Total de Processos" value={dashboard.summary.total_cards} color="purple" />
            <StatCard icon={Clock} label="Pendentes" value={dashboard.summary.pending} color="yellow" />
            <StatCard icon={CheckCircle} label="Aprovados" value={dashboard.summary.approved} color="green" />
            <StatCard icon={DollarSign} label="Receita Total" value={`€${dashboard.summary.total_revenue.toFixed(2)}`} color="blue" />
          </div>

          {/* Today Stats */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
            <h3 className="font-bold text-purple-900 mb-4">Actividade de Hoje</h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600">{dashboard.today.new_cards}</p>
                <p className="text-sm text-gray-600">Novos Pedidos</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">{dashboard.today.approved}</p>
                <p className="text-sm text-gray-600">Aprovados</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600">€{dashboard.today.revenue.toFixed(2)}</p>
                <p className="text-sm text-gray-600">Receita</p>
              </div>
            </div>
          </div>

          {/* Pending Signature Alert */}
          {dashboard.pending_signature > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <PenTool className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">{dashboard.pending_signature} cartão(ões) aguarda(m) a sua assinatura</p>
                  <p className="text-sm text-amber-700">Cartões aprovados pelos agentes que precisam da sua aprovação final</p>
                </div>
              </div>
              <button 
                onClick={() => setActiveTab('signatures')}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
              >
                Ver Cartões
              </button>
            </div>
          )}

          {/* Monthly Revenue Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Evolução Mensal</h3>
            <div className="flex items-end justify-between h-48 gap-2">
              {dashboard.monthly_revenue.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-purple-500 rounded-t transition-all hover:bg-purple-600"
                    style={{ height: `${Math.max((m.revenue / Math.max(...dashboard.monthly_revenue.map(x => x.revenue || 1))) * 140, 20)}px` }}
                  />
                  <p className="text-xs text-gray-500 mt-2">{m.month.split(' ')[0]}</p>
                  <p className="text-xs font-medium text-gray-700">€{m.revenue.toFixed(0)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Signatures Tab */}
      {activeTab === 'signatures' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200 bg-gray-50">
              <h3 className="font-bold text-gray-900">Cartões Aguardando Assinatura do Cônsul</h3>
              <p className="text-sm text-gray-500">Cartões aprovados pelos agentes consulares que precisam da sua assinatura final</p>
            </div>
            
            {pendingSignatures.length === 0 ? (
              <div className="p-10 text-center">
                <CheckCircle className="mx-auto text-green-400 mb-3" size={48} />
                <p className="text-gray-500">Não há cartões pendentes de assinatura</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {pendingSignatures.map(card => (
                  <div key={card.id} className="p-5 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm font-bold text-purple-600">{card.reference_code}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[card.status]}`}>
                          {STATUS_LABELS[card.status]}
                        </span>
                        {card.payment_status === 'paid' && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">Pago</span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900">{card.full_name}</p>
                      <p className="text-sm text-gray-500">{card.email} • {card.passport_number}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/consular-cards/${card.id}/preview-pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50"
                        data-testid={`preview-pdf-${card.id}`}
                      >
                        <Eye size={14} /> Rascunho
                      </a>
                      <button
                        onClick={() => setSelectedCard(card)}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                        data-testid={`sign-card-${card.id}`}
                      >
                        <PenTool size={16} />
                        Assinar e Emitir
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agents Tab */}
      {activeTab === 'agents' && dashboard && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Consular Agents */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-5 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <UserCheck size={18} className="text-indigo-500" />
                  Agentes Consulares
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboard.agents_productivity.length === 0 ? (
                  <p className="p-5 text-center text-gray-400">Nenhum agente consular registado</p>
                ) : (
                  dashboard.agents_productivity.map((agent, i) => (
                    <div key={i} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold">
                          {agent.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{agent.name}</p>
                          <p className="text-xs text-gray-500">Agente Consular</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm"><span className="text-green-600 font-bold">{agent.approved}</span> aprovados</p>
                        <p className="text-xs text-gray-500">{agent.processed} processados</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Financial Team */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-5 border-b border-gray-200">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <DollarSign size={18} className="text-amber-500" />
                  Equipa Financeira
                </h3>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboard.financial_team.length === 0 ? (
                  <p className="p-5 text-center text-gray-400">Nenhum membro financeiro registado</p>
                ) : (
                  dashboard.financial_team.map((member, i) => (
                    <div key={i} className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold">
                        {member.full_name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{member.full_name}</p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Productivity Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Resumo de Produtividade</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Agente</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Processados</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Aprovados</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Rejeitados</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Taxa Aprovação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dashboard.agents_productivity.map((agent, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3 font-medium text-gray-900">{agent.name}</td>
                      <td className="px-4 py-3 text-center">{agent.processed}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">{agent.approved}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">{agent.rejected}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          agent.processed > 0 && (agent.approved / agent.processed) >= 0.8 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {agent.processed > 0 ? Math.round((agent.approved / agent.processed) * 100) : 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Financial Tab */}
      {activeTab === 'financial' && financialData && (
        <div className="space-y-6">
          {/* Period Selector */}
          <div className="flex gap-2">
            {[
              { id: 'week', label: 'Esta Semana' },
              { id: 'month', label: 'Este Mês' },
              { id: 'quarter', label: 'Trimestre' },
              { id: 'year', label: 'Ano' },
            ].map(p => (
              <button
                key={p.id}
                onClick={() => fetchFinancialPeriod(p.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  financialData.period === p.id ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Financial Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="Receita Total" value={`€${financialData.total_revenue.toFixed(2)}`} color="green" />
            <StatCard icon={FileCheck} label="Transacções" value={financialData.total_transactions} color="blue" />
            <StatCard icon={Clock} label="Receita Pendente" value={`€${financialData.pending_revenue.toFixed(2)}`} color="yellow" />
            <StatCard icon={TrendingUp} label="Média/Transacção" value={`€${financialData.average_transaction.toFixed(2)}`} color="purple" />
          </div>

          {/* Revenue by Service */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Receita por Serviço</h3>
            <div className="space-y-4">
              {financialData.revenue_by_service.map((service, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <FileText className="text-purple-600" size={16} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{service.service}</p>
                      <p className="text-xs text-gray-500">{service.count} transacções</p>
                    </div>
                  </div>
                  <p className="font-bold text-green-600">€{service.revenue.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Report Generator */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-bold text-gray-900 mb-4">Gerar Relatório para MIREX</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                <input
                  type="date"
                  value={reportPeriod.start}
                  onChange={(e) => setReportPeriod(p => ({ ...p, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                <input
                  type="date"
                  value={reportPeriod.end}
                  onChange={(e) => setReportPeriod(p => ({ ...p, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={generateReport}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
                >
                  <BarChart3 size={16} />
                  Gerar Relatório
                </button>
              </div>
            </div>
          </div>

          {/* Report Results */}
          {reports && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-200 bg-purple-50 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-purple-900">Relatório Gerado</h3>
                  <p className="text-sm text-purple-700">Período: {reports.report_period.start} a {reports.report_period.end}</p>
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                  <Download size={16} />
                  Exportar PDF
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Cards Summary */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Resumo de Processos</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-gray-900">{reports.cards_summary.total}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    {Object.entries(reports.cards_summary.by_status).map(([status, count]) => (
                      <div key={status} className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{count}</p>
                        <p className="text-xs text-gray-500">{STATUS_LABELS[status] || status}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Financial Summary */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Resumo Financeiro</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-green-700">€{reports.financial_summary.total_revenue.toFixed(2)}</p>
                      <p className="text-sm text-green-600">Receita Total</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-3xl font-bold text-blue-700">€{reports.financial_summary.average_per_card.toFixed(2)}</p>
                      <p className="text-sm text-blue-600">Média por Processo</p>
                    </div>
                  </div>
                </div>

                {/* Appointments Summary */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Resumo de Agendamentos</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-xl font-bold text-gray-900">{reports.appointments_summary.total}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-xl font-bold text-blue-700">{reports.appointments_summary.confirmed}</p>
                      <p className="text-xs text-blue-600">Confirmados</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-xl font-bold text-green-700">{reports.appointments_summary.completed}</p>
                      <p className="text-xs text-green-600">Concluídos</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <p className="text-xl font-bold text-red-700">{reports.appointments_summary.cancelled}</p>
                      <p className="text-xs text-red-600">Cancelados</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Signature Modal */}
      {selectedCard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <PenTool className="text-purple-600" size={20} />
                Assinar Cartão Consular
              </h2>
            </div>
            <div className="p-5">
              <div className="bg-gray-50 rounded-lg p-4 mb-5">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Referência</p>
                    <p className="font-mono font-bold text-purple-600">{selectedCard.reference_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Nome</p>
                    <p className="font-medium">{selectedCard.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Passaporte</p>
                    <p className="font-mono">{selectedCard.passport_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Pagamento</p>
                    <p className={selectedCard.payment_status === 'paid' ? 'text-green-600 font-medium' : 'text-yellow-600'}>
                      {selectedCard.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações (opcional)</label>
                <textarea
                  value={signatureNotes}
                  onChange={(e) => setSignatureNotes(e.target.value)}
                  placeholder="Adicione observações à assinatura..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-5">
                <p className="text-sm text-purple-800">
                  Ao assinar, confirma que revisou este processo e autoriza a emissão oficial do Cartão Consular.
                </p>
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setSelectedCard(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancelar
              </button>
              <button
                onClick={() => handleSignCard(selectedCard.id)}
                disabled={signing}
                className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                {signing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    A assinar...
                  </>
                ) : (
                  <>
                    <PenTool size={16} />
                    Assinar Cartão
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-100',
    red: 'bg-red-50 text-red-600 border-red-100',
  };
  
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon size={20} />
        <span className="text-sm font-medium opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
